import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import type { IAnnotation, IArea, IPoint, IStroke, TAreaType } from '@datatype/gridgio/type';

interface IProps {
  areaType: TAreaType | null;
  workArea: IArea | null;
  keyPlanArea: IArea | null;
  cropArea: IArea | null;
  selectedIssueId: string | null;
  selectedAnnotationId: string | null;
  selectionEnabled: boolean;
  annotation: IAnnotation[];
  handleAreaChange: (type: TAreaType, area: IArea) => void;
  handleAnnotationComplete: (issueId: string, mark: IAnnotation['mark']) => void;
  handleAnnotationSelect: (annotationId: string | null) => void;
}

const Wrapper = styled.svg<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  cursor: ${({ $active }) => ($active ? 'crosshair' : 'default')};
  pointer-events: ${({ $active }) => ($active ? 'auto' : 'none')};
  touch-action: none;
`;

const createArea = (start: IPoint, end: IPoint): IArea => {
  const left = Math.min(start.x, end.x);
  const top = Math.min(start.y, end.y);
  const right = Math.max(start.x, end.x);
  const bottom = Math.max(start.y, end.y);
  return {
    point: [
      { x: left, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
      { x: left, y: bottom },
    ],
  };
};

const getAreaBounds = (area: IArea) => {
  const x = area.point.map((point) => point.x);
  const y = area.point.map((point) => point.y);
  const left = Math.min(...x);
  const top = Math.min(...y);
  return { left, top, width: Math.max(...x) - left, height: Math.max(...y) - top };
};

const MIN_FREEHAND_LENGTH = 0.005;

const getStrokeLength = (point: IPoint[]) =>
  point.slice(1).reduce((length, position, index) => {
    const previous = point[index];
    return length + Math.hypot(position.x - previous.x, position.y - previous.y);
  }, 0);

const AnnotationLayer: React.FC<IProps> = ({
  areaType,
  workArea,
  keyPlanArea,
  cropArea,
  selectedIssueId,
  selectedAnnotationId,
  selectionEnabled,
  annotation,
  handleAreaChange,
  handleAnnotationComplete,
  handleAnnotationSelect,
}) => {
  const dragStartRef = useRef<IPoint | null>(null);
  const annotationPointsRef = useRef<IPoint[]>([]);
  const [draftArea, setDraftArea] = useState<IArea | null>(null);
  const [draftAnnotation, setDraftAnnotation] = useState<IPoint[]>([]);

  const getPoint = (event: React.PointerEvent<SVGSVGElement>): IPoint => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height)),
    };
  };

  const getDocumentPoint = (point: IPoint): IPoint => {
    const visibleArea = cropArea ? getAreaBounds(cropArea) : { left: 0, top: 0, width: 1, height: 1 };
    return {
      x: visibleArea.left + point.x * visibleArea.width,
      y: visibleArea.top + point.y * visibleArea.height,
    };
  };

  const getVisiblePoint = (point: IPoint): IPoint => {
    const visibleArea = cropArea ? getAreaBounds(cropArea) : { left: 0, top: 0, width: 1, height: 1 };
    return {
      x: (point.x - visibleArea.left) / visibleArea.width,
      y: (point.y - visibleArea.top) / visibleArea.height,
    };
  };

  const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    if (selectionEnabled && event.target === event.currentTarget) {
      handleAnnotationSelect(null);
      return;
    }
    if (!areaType && !selectedIssueId) return;
    event.preventDefault();
    const point = getPoint(event);
    if (areaType) {
      dragStartRef.current = point;
      setDraftArea(createArea(point, point));
    } else {
      const documentPoint = getDocumentPoint(point);
      annotationPointsRef.current = [documentPoint];
      setDraftAnnotation([documentPoint]);
    }
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (areaType && dragStartRef.current) {
      setDraftArea(createArea(dragStartRef.current, getPoint(event)));
      return;
    }
    if (!selectedIssueId || annotationPointsRef.current.length === 0) return;
    const point = getDocumentPoint(getPoint(event));
    annotationPointsRef.current = [...annotationPointsRef.current, point];
    setDraftAnnotation(annotationPointsRef.current);
  };

  const handlePointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
    if (areaType && dragStartRef.current) {
      const area = createArea(dragStartRef.current, getPoint(event));
      dragStartRef.current = null;
      setDraftArea(null);
      const bounds = getAreaBounds(area);
      if (bounds.width > 0.01 && bounds.height > 0.01) handleAreaChange(areaType, area);
      return;
    }
    if (!selectedIssueId) return;
    if (annotationPointsRef.current.length === 0) return;
    const points = [...annotationPointsRef.current, getDocumentPoint(getPoint(event))];
    annotationPointsRef.current = [];
    setDraftAnnotation([]);
    if (getStrokeLength(points) >= MIN_FREEHAND_LENGTH) {
      handleAnnotationComplete(selectedIssueId, { stroke: [{ point: points }] });
    }
  };

  const AnnotationLine = ({
    points,
    stroke,
    strokeWidth = '0.004',
  }: {
    points: IPoint[];
    stroke: string;
    strokeWidth?: string;
  }) => (
    <polyline
      points={points
        .map(getVisiblePoint)
        .map((point) => `${point.x},${point.y}`)
        .join(' ')}
      fill='none'
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  );

  const AreaRectangle = ({ area, stroke }: { area: IArea | null; stroke: string }) => {
    if (!area) return null;
    const bounds = getAreaBounds(area);
    return (
      <rect
        x={bounds.left}
        y={bounds.top}
        width={bounds.width}
        height={bounds.height}
        fill='transparent'
        stroke={stroke}
        strokeWidth='0.003'
        strokeDasharray='0.008 0.006'
      />
    );
  };

  return (
    <Wrapper
      viewBox='0 0 1 1'
      preserveAspectRatio='none'
      $active={areaType !== null || selectedIssueId !== null || selectionEnabled}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}>
      <AreaRectangle area={workArea} stroke={color.RED_2} />
      <AreaRectangle area={keyPlanArea} stroke={color.GREEN_4} />
      <AreaRectangle area={draftArea} stroke={color.CYAN_3} />
      {annotation.map((item) => (
        <g
          key={item.id}
          cursor={selectionEnabled ? 'pointer' : 'default'}
          onPointerDown={(event) => {
            if (selectionEnabled) event.stopPropagation();
          }}
          onClick={(event) => {
            if (!selectionEnabled) return;
            event.stopPropagation();
            handleAnnotationSelect(item.id);
          }}>
          {item.mark.stroke.map((mark, index) => (
            <React.Fragment key={`${item.id}-mark-${index}`}>
              {selectionEnabled && <AnnotationLine points={mark.point} stroke='transparent' strokeWidth='0.03' />}
              <AnnotationLine
                points={mark.point}
                stroke={item.id === selectedAnnotationId ? color.CYAN_2 : color.RED_2}
              />
            </React.Fragment>
          ))}
        </g>
      ))}
      {draftAnnotation.length > 0 && <AnnotationLine points={draftAnnotation} stroke={color.CYAN_2} />}
    </Wrapper>
  );
};

export default AnnotationLayer;
